import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core'
import { AsyncPipe, CommonModule } from '@angular/common'
import { BehaviorSubject, combineLatest, firstValueFrom, merge, Observable, of, ReplaySubject, Subject } from 'rxjs'
import { Announcement } from '../../../models/announcement'
import { UsersService } from '../../../services/domain/users.service'
import { IonicModule, ModalController } from '@ionic/angular'
import { PrivateMessage } from '../../../models/privateMessage'
import { HtmlEditorService, ImageService, LinkService, ToolbarService } from '@syncfusion/ej2-angular-richtexteditor'
import { MessageMode, MessageModes, MessageType } from './messageModes'
import { User } from '../../../models/user'
import { MessageEditorComponent } from '../../../features/messaging/message-editor/message-editor.component'
import { MessagesService } from '../../../services/messages/messages.service'
import { PrimeNgToastService } from '../../../services/prime-ng-toast/prime-ng-toast.service'
import { PrivateMessagesService } from '../../../services/domain/privateMessages/private-messages.service'
import { AnnouncementsService } from '../../../services/domain/announcements/announcements.service'
import { SmsMessage } from '../../../models/smsMessage'
import { SmsService } from '../../../services/domain/sms/sms.service'
import { Workgroup } from '../../../models/workgroup'
import { WorkgroupService } from '../../../services/domain/workgroup/workgroup.service'
import { first, scan, shareReplay, startWith, switchMap } from 'rxjs/operators'
import { MessageRecipients } from 'src/app/features/messaging/MessageRecipients'
import { ClientMessage } from 'src/app/models/clientMessage'
import { ClientMessagesHistoryService } from '../../../services/domain/clientMessagesHistory/client-messages-history.service'
import { MessagingPageService } from './messaging-page.service'
import { ToggleableModulesService } from '../../../features/config/toggleable-modules/toggleable-modules.service'
import { ToggleableModulesName } from '../../../features/config/toggleable-modules/ToggleableModules'
import { AnnouncementListComponent } from '../../../features/messaging/announcement-list/announcement-list.component'
import { PrivateMessageListWrapperComponent } from '../../../features/messaging/private-message-list-wrapper/private-message-list-wrapper.component'
import { SmsMessagesListComponent } from '../../../features/messaging/sms-messages-list/sms-messages-list.component'
import { ClientMessagesHistoryListComponent } from '../../../features/messaging/client-messages-history-list/client-messages-history-list.component'

class FabState {
  icon: 'add' | 'send' = 'add'

  public change(to) {
    this.icon = to
  }
}

export enum MessagingTabName {
  ANNOUNCEMENTS,
  PRIVATE_MESSAGES,
  SMS,
  CLIENT_HISTORY,
}

@Component({
  selector: 'app-messaging',
  standalone: true,
  imports: [
    CommonModule,
    IonicModule,
    AsyncPipe,
    AnnouncementListComponent,
    PrivateMessageListWrapperComponent,
    SmsMessagesListComponent,
    ClientMessagesHistoryListComponent,
    MessageEditorComponent,
  ],
  providers: [ToolbarService, LinkService, ImageService, HtmlEditorService],
  templateUrl: './messaging-page.component.html',
  styleUrls: ['./messaging-page.component.scss'],
})
export class MessagingPage implements OnInit, OnDestroy {
  private userService = inject(UsersService)
  private modalCtrl = inject(ModalController)
  private messagesService = inject(MessagesService)
  private toaster = inject(PrimeNgToastService)
  private privMsgService = inject(PrivateMessagesService)
  private announcementsService = inject(AnnouncementsService)
  private smsService = inject(SmsService)
  private workgroupsService = inject(WorkgroupService)
  private clientMessageHistoryService = inject(ClientMessagesHistoryService)
  private toggleableModulesService = inject(ToggleableModulesService)
  service = inject(MessagingPageService)

  @ViewChild('editor') editor: MessageEditorComponent

  announcements$: Observable<Announcement[]>
  announcementsUnreadCount$: Observable<number>

  inboundPrivateMessages$ = this.service.inboundPrivateMessages$
  outboundPrivateMessages$ = this.service.outboundPrivateMessages$
  privateMessagesUnreadCount$: Observable<number>

  inboundSmsMessages$: Observable<SmsMessage[]>
  outboundSmsMessages$: Observable<SmsMessage[]>
  // smsMessages$: Observable<SmsMessage[]>
  smsMessagesUnreadCount$: Observable<number>

  clientOutboundMessages$: Observable<ClientMessage[]>

  activeMsgMode: MessageMode = MessageModes.getByType(MessageType.announcement)
  fabState: FabState = new FabState()

  editorShown = false

  announcementsAllowed = false
  smsAllowed = false
  clientHistoryAllowed = false

  MessagingTabName = MessagingTabName
  MessageType = MessageType
  activeTab: MessagingTabName

  users$: Observable<User[]>
  currentUser$: Observable<User>
  workgroups$: Observable<Workgroup[]>

  smsWorkgroupFilter$$ = new BehaviorSubject<Workgroup | null>(null)
  smsWorkgroupFilter$ = this.smsWorkgroupFilter$$.asObservable()
  inboundSmsLoadWithOffset$$ = new ReplaySubject<number>(1)
  inboundSmsLoadWithOffset$ = this.inboundSmsLoadWithOffset$$.asObservable()
  outboundSmsLoadWithOffset$$ = new ReplaySubject<number>(1)
  outboundSmsLoadWithOffset$ = this.outboundSmsLoadWithOffset$$.asObservable()

  clientOutboundMessagesWithOffset$$ = new ReplaySubject<number>(1)
  clientOutboundMessagesWithOffset$ = this.clientOutboundMessagesWithOffset$$.asObservable()

  private destroy$ = new Subject<void>()

  private TAG = 'MessagingPageComponent'

  private cd = inject(ChangeDetectorRef)

  constructor() {}

  ngOnInit() {
    this.announcements$ = this.messagesService.getAnnouncementsForCurrentUser()

    this.inboundSmsMessages$ = merge(this.smsWorkgroupFilter$, this.userService.getCurrentUserWorkgroup()).pipe(
      switchMap((workgroup) => {
        return this.inboundSmsLoadWithOffset$.pipe(
          switchMap((offset) =>
            this.smsService.getLatestInboundSmsForWorkgroupPaginated(workgroup, 'paginatedList', 20, offset),
          ),
          scan((acc, value) => {
            acc = [...acc, ...value]
            return acc
          }, []),
        )
      }),
    )

    this.outboundSmsMessages$ = merge(this.smsWorkgroupFilter$, this.userService.getCurrentUserWorkgroup()).pipe(
      switchMap((workgroup) => {
        return this.outboundSmsLoadWithOffset$.pipe(
          switchMap((offset) =>
            this.smsService.getLatestOutboundSmsForWorkgroupPaginated(workgroup, 'paginatedList', 20, offset),
          ),
          scan((acc, value) => {
            acc = [...acc, ...value]
            return acc
          }, []),
        )
      }),
    )

    this.clientOutboundMessages$ = this.clientOutboundMessagesWithOffset$.pipe(
      switchMap((offset) => this.clientMessageHistoryService.getMessagesPaginated(20, offset)),
      scan((acc, value) => [...acc, ...value], []),
    )

    this.announcementsUnreadCount$ = this.announcementsService.getUnreadCountForCurrentUser()
    this.privateMessagesUnreadCount$ = this.privMsgService.getUnreadCountForCurrentUser()
    this.userService
      .getCurrentUserWorkgroup()
      .pipe(first())
      .subscribe((wg) => {
        if (!this.smsWorkgroupFilter$$.value) {
          this.smsWorkgroupFilter$$.next(wg)
        }
      })

    const currentSmsWorkgroup$ = this.smsWorkgroupFilter$.pipe(shareReplay({ bufferSize: 1, refCount: true }))

    this.smsMessagesUnreadCount$ = combineLatest([
      currentSmsWorkgroup$,
      this.smsService.unreadCountForCurrentUser$.pipe(startWith(0)),
    ]).pipe(switchMap(([workgroup]) => this.smsService.getUnreadCountForCurrentUser(workgroup)))

    this.users$ = this.userService.getAllWorkersAndAssistants$()
    this.currentUser$ = combineLatest([
      this.userService.getCurrentUser(),
      this.toggleableModulesService.isActive(ToggleableModulesName.SMS),
    ]).pipe(
      switchMap(([user, isSmsAllowedInConf]) => {
        this.smsAllowed = isSmsAllowedInConf && user.isInRoleGroup(MessageModes.getByType(MessageType.sms).allowedRoles)
        this.clientHistoryAllowed = user.isAdmin() || user.isOaCoordinator()
        this.announcementsAllowed = user.isWorker() || user.isOaAssistant() || user.isSrManager()
        this.setInitialActiveTab()
        this.cd.detectChanges()
        return of(user)
      }),
    )

    this.workgroups$ = this.workgroupsService.getAllWithCaching()
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  switchActiveMsgMode(msgMode?: MessageMode) {
    if (msgMode) this.activeMsgMode = msgMode
  }

  async fabClicked() {
    if (this.editorShown) {
      await this.editor.send()
    } else {
      this.showEditor()
    }
  }

  async dismissModal() {
    const wg = await firstValueFrom(this.userService.getCurrentUserWorkgroup())
    await this.modalCtrl.dismiss()
    this.smsService.updateUnreadCount(wg)
  }

  showEditor() {
    this.editorShown = true
    this.fabState.change('send')
  }

  hideEditor() {
    this.editorShown = false
    this.fabState.change('add')
  }

  async sendMessage($event) {
    console.log(this.TAG, 'sendMessage', $event)
    try {
      await this.messagesService.send($event)
      this.editor.clear()
      this.hideEditor()
    } catch (e) {
      console.error(`createNewMessage ${e} ${$event}`)
      this.toaster.displayCustomToast('error', 'Chyba při odesílání zprávy. Zkuste to prosím znovu.')
    }
  }

  switchTabTo(tabName: MessagingTabName) {
    this.activeTab = tabName

    this.switchActiveMsgMode(MessageModes.getByTabName(tabName))
  }

  replyToPrivateMessage(msg: PrivateMessage) {
    // open editor
    this.activeMsgMode = MessageModes.getByType(MessageType.privateMessage)
    this.showEditor()
    // fill in reply details (parentId)
    if (msg.isFeedback) {
      this.userService
        .getUserByAssistantId(msg.assistance_feedback_message.assignment.assistantId)
        .pipe(first())
        .subscribe((recipient) => {
          if (msg.parentMessage) recipient = msg.publishedByUser
          console.log('fill in reply details, recipient:', recipient, 'msg:', msg)
          this.editor.fillForReply(msg, new MessageRecipients(recipient))
        })
    } else {
      this.editor.fillForReply(msg)
    }
  }

  smsLoadInboundOffset(offset: number) {
    this.inboundSmsLoadWithOffset$$.next(offset)
  }

  smsLoadOutboundOffset(offset: number) {
    this.outboundSmsLoadWithOffset$$.next(offset)
  }

  clientOutboundMessagesLoadOffset(offset: number) {
    this.clientOutboundMessagesWithOffset$$.next(offset)
  }

  smsWorkgroupFilterChanged(wg: Workgroup) {
    this.smsWorkgroupFilter$$.next(wg || null)
  }

  async smsMessageRead(msg: SmsMessage) {
    const workgroup = this.smsWorkgroupFilter$$.value
    await this.smsService.markRead(msg, workgroup)
  }

  async smsMessagesReadAll() {
    const workgroup = this.smsWorkgroupFilter$$.value
    await this.smsService.markAllAsRead(workgroup)
  }

  private setInitialActiveTab() {
    if (this.announcementsAllowed) {
      this.switchTabTo(MessagingTabName.ANNOUNCEMENTS)
    } else {
      this.switchTabTo(MessagingTabName.PRIVATE_MESSAGES)
    }
  }
}
